import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useAppStore } from '@/stores/appStore';
import {
  recommendationsApi,
  socialAccountsApi,
  type RecommendationCandidate,
  type RecommendationsResponse,
} from '@/lib/api';
import { PLATFORMS } from '@/lib/platforms';
import { Compass, Sparkles, X, Clock, Hash, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button, IconButton } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { PlatformIcon } from '@/components/ui/PlatformIcon';

/**
 * For You — the recommendations engine (analytics + inbox + trends + post
 * history fused through the x-algorithm-style mixer) surfaced as a feed of
 * actionable suggestions. Feedback persists server-side: dismissed items never
 * return, used items count as wins, served items rest a few days.
 */
export function ForYouFeed() {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['for-you', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return recommendationsApi.forYou(token!, { workspaceId: currentWorkspace!.id, limit: 6 });
    },
    enabled: !!currentWorkspace,
    staleTime: 5 * 60 * 1000,
  });

  // Shares the cache with the rest of the app; decides which empty state is true.
  const { data: accountsData } = useQuery({
    queryKey: ['social-accounts', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return socialAccountsApi.list(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });
  const hasAccounts = (accountsData?.accounts.length ?? 0) > 0;

  const feedback = useMutation({
    mutationFn: async ({
      candidate,
      event,
    }: {
      candidate: RecommendationCandidate;
      event: 'used' | 'dismissed';
    }) => {
      const token = await getToken();
      return recommendationsApi.feedback(token!, {
        workspaceId: currentWorkspace!.id,
        candidateId: candidate.id,
        contentHash: candidate.content_hash,
        event,
      });
    },
    // Drop the row locally. Invalidating refetched the mixer, which marked a
    // fresh batch as served and burned the next set of suggestions.
    onSuccess: (_data, { candidate }) => {
      qc.setQueryData<RecommendationsResponse>(['for-you', currentWorkspace?.id], (prev) =>
        prev
          ? { ...prev, candidates: prev.candidates.filter((row) => row.id !== candidate.id) }
          : prev
      );
    },
  });

  const useCandidate = (candidate: RecommendationCandidate) => {
    feedback.mutate({ candidate, event: 'used' });
    const hashtags = (candidate.hashtags ?? []).map((h) => (h.startsWith('#') ? h : `#${h}`));
    const prefillContent = candidate.text
      ? `${candidate.text}${hashtags.length ? `\n\n${hashtags.join(' ')}` : ''}`
      : `${candidate.topic ?? ''}${hashtags.length ? `\n\n${hashtags.join(' ')}` : ''}`.trim();
    navigate('/publishing?compose=true', { state: { prefillContent } });
  };

  const candidates = (data?.candidates ?? []).slice(0, 6);

  const header = (
    <CardHeader
      icon={Compass}
      title="For You"
      description="Ranked from your brand kit, post performance, inbox activity and live trends"
      action={
        <IconButton label="Refresh suggestions" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
        </IconButton>
      }
    />
  );

  if (!isLoading && candidates.length === 0) {
    return (
      <Card data-tour="for-you">
        {header}
        <div className="border-t border-hairline">
          {hasAccounts ? (
            <EmptyState
              compact
              icon={Compass}
              title="You're all caught up"
              body="Nothing new to suggest right now. Fresh ideas arrive as you publish and as this week's trends land."
            />
          ) : (
            <EmptyState
              compact
              icon={Compass}
              title="Suggestions start with your data"
              body="Connect this brand's social accounts. Enflux learns from their history and this week's trends, then ranks what to post next."
              action={
                <Button variant="secondary" onClick={() => navigate('/settings/social-accounts')}>
                  Connect accounts
                </Button>
              }
            />
          )}
        </div>
      </Card>
    );
  }

  const KindIcon = (c: RecommendationCandidate) =>
    c.kind === 'time_slot' ? Clock : c.kind === 'hashtag' ? Hash : Sparkles;

  return (
    <Card data-tour="for-you">
      {header}
      <ul className="divide-y divide-hairline border-t border-hairline">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <li key={i} className="flex items-start gap-3 px-5 py-4" aria-busy="true">
                <span className="sfx-skeleton h-7 w-7 shrink-0 rounded-full" />
                <div className="flex-1 space-y-2">
                  <span className="sfx-skeleton block h-3.5 w-3/4" />
                  <span className="sfx-skeleton block h-3 w-1/3" />
                </div>
              </li>
            ))
          : candidates.map((c) => {
              const draftable = c.kind === 'content' || c.kind === 'topic' || c.kind === 'hashtag';
              const isFollowHint = (c.topic ?? '').startsWith('Follow ');
              const Icon = KindIcon(c);
              return (
                <li key={c.id} className="flex items-start gap-3 px-5 py-3.5">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-tertiary/70 text-accent ring-1 ring-inset ring-hairline">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-sm text-foreground">
                      {c.text || c.topic || (c.hashtags ?? []).join(' ') || 'Suggested post'}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      {c.reason && (
                        <span className="mr-0.5 text-[11px] text-muted-foreground">{c.reason}</span>
                      )}
                      {(c.platforms ?? []).slice(0, 3).map((p) => (
                        <Badge key={p}>
                          <PlatformIcon platform={p} size="xs" variant="glyph" />
                          {PLATFORMS[p as keyof typeof PLATFORMS]?.label ?? p}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {draftable && !isFollowHint && (
                      <Button
                        variant="accent-soft"
                        onClick={() => useCandidate(c)}
                        disabled={feedback.isPending}
                      >
                        <Sparkles className="h-3 w-3" />
                        Draft this
                      </Button>
                    )}
                    <IconButton
                      label="Not interested"
                      tone="danger"
                      onClick={() => feedback.mutate({ candidate: c, event: 'dismissed' })}
                      disabled={feedback.isPending}
                    >
                      <X className="h-3.5 w-3.5" />
                    </IconButton>
                  </div>
                </li>
              );
            })}
      </ul>
    </Card>
  );
}
